import { FC, useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { dataFunction } from '../../assets/data'
import { dataFunctionFR } from '../../assets/data-fr'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'

// import CountUp, { useCountUp } from 'react-countup'
// import Counter from '../Counter'

interface ChapterProps {
  colorMode: 'light' | 'dark'
  tenant: Tenant
  headerHeight?: number
}

const Chapter: FC<ChapterProps> = ({ colorMode, tenant, headerHeight }) => {
  // const data = dataFunction(colorMode)
  const data =
    tenant?.lang === 'fr' ? dataFunctionFR(colorMode) : dataFunction(colorMode)
  // State to store the active title index
  const [activeChapterIndex, setActiveChapterIndex] = useState<number>(0)
  const addHeight = activeChapterIndex < data.length - 1
  const addPadding = activeChapterIndex === data.length - 1
  const addPaddingTwo = activeChapterIndex === data.length - 2

  //   console.log('index', activeChapterIndex)

  // Ref to store all our title refs
  const ref = useRef<(HTMLDivElement | null)[]>([])

  // Function to add elements to our refs array
  const addToRefs = (el: any) => {
    if (el && !ref.current.includes(el)) {
      ref.current.push(el)
    }
  }

  useEffect(() => {
    // This is the callback that gets invoked whenever an observed entry intersects with the viewport
    const observerCallback = (entries: any, observer: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          const titleIndex = parseInt(
            entry.target.getAttribute('data-index'),
            10
          )
          setActiveChapterIndex(titleIndex)
        }
      })
    }

    const current = ref.current

    // console.log('headerHeight', headerHeight)

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    }

    // Instantiate IntersectionObserver
    const observer = new IntersectionObserver(observerCallback, observerOptions)

    if (ref.current) {
      ref.current.forEach((el) => {
        if (el !== null) {
          observer.observe(el)
        }
      })
    }

    return () => {
      if (current) {
        current.forEach((el) => {
          if (el) {
            observer.unobserve(el)
          }
        })
      }
    }
  }, [])

  return (
    <ChapterWrapper>
      <StickyElement
        headerHeight={headerHeight}
        addPadding={addPadding}
        addPaddingTwo={addPaddingTwo}
        className={colorMode === 'dark' ? 'dark' : ''}
      >
        {/* <Counter value={Data[activeChapterIndex].absolutNumber}  previousValue={Data[activeChapterIndex - 1]?.absolutNumber} nextValue={Data[activeChapterIndex - 1]?.absolutNumber}></Counter> */}
        <AbsolutNumberWrapper addHeight={addHeight}>
          <div>
            {' '}
            <div className='title'>
              {data[activeChapterIndex].chartTitle.join(' ')}
            </div>
            <div className='number'>
              {data[activeChapterIndex].absolutNumber}
            </div>
          </div>
        </AbsolutNumberWrapper>

        <BylineWrapper>
          {data[activeChapterIndex].chartParameters &&
            data[activeChapterIndex].chartParameters.map(
              (parameter: any, index: number) => {
                return parameter.barByline &&
                  parameter.barByline.trim() !== '' ? (
                  <div
                    key={index}
                    className={
                      parameter.barWidth <= 10
                        ? 'bar byline top smooth'
                        : 'bar byline top noTitle'
                    }
                    style={{
                      width: `${parameter.barWidth}%`,
                      height: `${
                        parameter.barByline.trim() !== '' ? '0%' : 'auto'
                      }`,
                      // backgroundColor: parameter.barColor || '#202346', // default color
                    }}
                  >
                    {index % 2 === 0
                      ? parameter.barWidth <= 10 && (
                          <span className={parameter.barByline}>
                            {parameter.barByline}:{' '}
                            {parameter.barWidth.toString().replace(/\./g, ',')}%
                          </span>
                        )
                      : null}
                  </div>
                ) : null
              }
            )}
        </BylineWrapper>

        <ChartWrapper>
          {data[activeChapterIndex].chartParameters &&
            data[activeChapterIndex].chartParameters.map(
              (parameter: any, index: number) => {
                return (
                  <div
                    key={index}
                    className={
                      parameter.barWidth <= 10
                        ? 'bar noTitle'
                        : index >= 2
                        ? 'bar fadeOut' + (colorMode === 'dark' ? ' dark' : '')
                        : 'bar' + (colorMode === 'dark' ? ' dark' : '')
                    }
                    style={{
                      width: `${parameter.barWidth}%`,
                      backgroundColor:
                        colorMode === 'dark' &&
                        parameter?.barColor === '#202346'
                          ? '#fff'
                          : parameter?.barColor || '#202346',
                    }}
                  >
                    {parameter.barWidth <= 10 ? null : (
                      <span key={index + parameter.barWidth}>
                        {parameter.barWidth.toString().replace(/\./g, ',')}%
                      </span>
                    )}
                    <span key={index + parameter.barTitle}>
                      {parameter.barTitle}
                    </span>
                  </div>
                )
              }
            )}
        </ChartWrapper>

        <BylineWrapper>
          {data[activeChapterIndex].chartParameters &&
            data[activeChapterIndex].chartParameters.map(
              (parameter: any, index: number) => {
                return (
                  parameter.barByline && (
                    <div
                      key={index}
                      className={
                        parameter.barWidth <= 10
                          ? 'bar byline bottom'
                          : 'bar byline bottom noTitle'
                      }
                      style={{
                        width: `${parameter.barWidth}%`,
                        // backgroundColor: parameter.barColor || '#202346', // default color
                      }}
                    >
                      {index % 2 !== 0
                        ? parameter.barWidth <= 10 && (
                            <span className={parameter.barByline}>
                              {parameter.barByline}:{' '}
                              {parameter.barWidth
                                .toString()
                                .replace(/\./g, ',')}
                              %
                            </span>
                          )
                        : null}
                    </div>
                  )
                )
              }
            )}
        </BylineWrapper>
      </StickyElement>

      {data &&
        data.map((chapter: any, index: number) => {
          return (
            <>
              <TextWrapper className={index === 0 ? 'first' : ''}>
                <div>
                  <Title
                    key={index}
                    ref={addToRefs} // Add ref to each title
                    data-index={index}
                    className={colorMode === 'dark' ? 'dark' : ''}
                  >
                    {chapter.chapterTitle}
                  </Title>
                  <div />
                  {chapter.htmlParagraph}
                </div>
              </TextWrapper>
            </>
          )
        })}
    </ChapterWrapper>
  )
}

const ChapterWrapper = styled.div`
  width: 100%;
`

const StickyElement = styled.div<{
  headerHeight?: number
  addPadding: boolean
  addPaddingTwo: boolean
}>`
  position: sticky;
  top: ${({ headerHeight }) => headerHeight ?? 0}px;
  /* width: 100%; */
  padding: 1.2rem 0;
  padding-bottom: ${(props) =>
    props.addPadding ? '15px' : props.addPaddingTwo ? '45px' : '69px'};
  background-color: var(--site-background);
  box-shadow: 0px 10px 10px -8px rgba(0, 0, 0, 0.05);
  &.dark {
    box-shadow: rgb(48 45 45) 0px 10px 10px -8px;
  }
  /* @media only screen and (min-width: 600px) {
  box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 50px;
  padding: 1em 1.5em;
  border-radius: 5px;
  } */
  @media only screen and (max-width: 599px) {
    padding-bottom: ${(props) =>
      props.addPadding ? '15px' : props.addPaddingTwo ? '45px' : '66px'};
  }
`

const TextWrapper = styled.div`
  img {
    width: 40px;
  }
  height: 100vh;
  font-family: var(--font-serif);
  display: inline-block;
  p > span {
    font-weight: 600;
    font-family: var(--font-sans-serif);
    padding: 2px 5px;
    border-radius: 3px;
    color: #fff;
    &.SVP {
      background-color: #4b8a3e;
    }
    &.SP {
      background-color: #f0554d;
    }
    &.Mitte {
      background-color: #d6862b;
    }
    &.FDP {
      background-color: #3872b5;
    }
    &.GPS,
    &.Grüne,
    &.Grünen {
      background-color: #84b547;
    }
    &.GLP {
      background-color: #c4c43d;
    }
    &.Andere {
      background-color: #7d7d7d;
    }
  }

  &.first > div {
    padding-top: 2em;
  }

  /* >div {
    padding: 1em 1em;
    background-color: #F0F0F2;
  } */

  /* styles */
`

const Title = styled.h4`
  font-family: var(--font-sans-serif);

  ::before {
    display: block;
    order: -2;
    width: 6rem;
    margin: 0 auto 4rem;
    border-bottom: 2px solid var(--gray-4);
    content: '';
  }
  &.dark::before {
    border-bottom: 2px solid var(--gray-2);
  }
  margin-top: 2em;

  font-size: 1.2em;
  line-height: 2.6rem;
`

const AbsolutNumberWrapper = styled.div<{ addHeight: boolean }>`
  display: flex;
  /* flex-direction: column; */
  justify-content: center;
  flex-direction: row;
  align-items: center;

  //set minheight to the height of number
  min-height: ${(props) => (props.addHeight ? '72px' : '0')};
  @media only screen and (max-width: 599px) {
    min-height: ${(props) => (props.addHeight ? '77.34px' : '0')};
  }
  line-height: 1em;
  margin-bottom: 1.125rem;

  div {
    display: flex;
    align-items: baseline;
    @media only screen and (max-width: 599px) {
      flex-direction: column;
      text-align: center;
      align-items: center;
    }
  }

  .number {
    font-size: 1.2em;
    margin-left: 0.75rem;
    @media only screen and (max-width: 599px) {
      font-size: 1.1em;
      margin: 0;
      padding-top: 0.5em;
    }
  }
  .title {
    font-size: 1.5rem;
    font-weight: 600;
    @media only screen and (max-width: 599px) {
      font-size: 1.5rem;
    }
  }
  /* styles */
`

const BylineWrapper = styled.div`
  display: flex;
  /* margin-top: 1rem; */

  .bar {
    position: relative;
    box-sizing: border-box;
    &.byline {
      font-size: 1.2rem;
      height: 12px;
      display: flex;
      line-height: 1.2em;
      /* margin: 0.75em 0; */
      letter-spacing: 0.25px;
    }

    &.byline.top {
      align-self: self-end;
      padding-left: 0.5rem;
    }

    &.byline.bottom {
      align-self: self-start;
      position: relative;
      padding-left: 0.5rem;

      span {
        margin-top: -1em;
      }
    }

    &.byline.top:nth-child(odd) {
      --stroke-color: var(--site-background);
      --stroke-width: 2px;
      letter-spacing: 0.5px;
      color: var(--text-color);
      text-shadow: var(--stroke-width) 0 0 var(--stroke-color),
        calc(var(--stroke-width) * -1) 0 0 var(--stroke-color),
        0 var(--stroke-width) 0 var(--stroke-color),
        0 calc(var(--stroke-width) * -1) 0 var(--stroke-color);

      text-shadow: calc(var(--stroke-width) * 1) calc(var(--stroke-width) * 0) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.9239) calc(var(--stroke-width) * 0.3827) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.7071) calc(var(--stroke-width) * 0.7071) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.3827) calc(var(--stroke-width) * 0.9239) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0) calc(var(--stroke-width) * 1) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.3827) calc(var(--stroke-width) * 0.9239) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.7071) calc(var(--stroke-width) * 0.7071) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.9239) calc(var(--stroke-width) * 0.3827) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -1) calc(var(--stroke-width) * 0) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.9239) calc(var(--stroke-width) * -0.3827)
          0 var(--stroke-color),
        calc(var(--stroke-width) * -0.7071) calc(var(--stroke-width) * -0.7071)
          0 var(--stroke-color),
        calc(var(--stroke-width) * -0.3827) calc(var(--stroke-width) * -0.9239)
          0 var(--stroke-color),
        calc(var(--stroke-width) * 0) calc(var(--stroke-width) * -1) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.3827) calc(var(--stroke-width) * -0.9239) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.7071) calc(var(--stroke-width) * -0.7071) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.9239) calc(var(--stroke-width) * -0.3827) 0
          var(--stroke-color);
    }

    &.byline.bottom:nth-child(even) {
      --stroke-color: var(--site-background);
      --stroke-width: 2px;
      letter-spacing: 0.5px;
      color: var(--text-color);
      text-shadow: var(--stroke-width) 0 0 var(--stroke-color),
        calc(var(--stroke-width) * -1) 0 0 var(--stroke-color),
        0 var(--stroke-width) 0 var(--stroke-color),
        0 calc(var(--stroke-width) * -1) 0 var(--stroke-color);

      text-shadow: calc(var(--stroke-width) * 1) calc(var(--stroke-width) * 0) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.9239) calc(var(--stroke-width) * 0.3827) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.7071) calc(var(--stroke-width) * 0.7071) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.3827) calc(var(--stroke-width) * 0.9239) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0) calc(var(--stroke-width) * 1) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.3827) calc(var(--stroke-width) * 0.9239) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.7071) calc(var(--stroke-width) * 0.7071) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.9239) calc(var(--stroke-width) * 0.3827) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -1) calc(var(--stroke-width) * 0) 0
          var(--stroke-color),
        calc(var(--stroke-width) * -0.9239) calc(var(--stroke-width) * -0.3827)
          0 var(--stroke-color),
        calc(var(--stroke-width) * -0.7071) calc(var(--stroke-width) * -0.7071)
          0 var(--stroke-color),
        calc(var(--stroke-width) * -0.3827) calc(var(--stroke-width) * -0.9239)
          0 var(--stroke-color),
        calc(var(--stroke-width) * 0) calc(var(--stroke-width) * -1) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.3827) calc(var(--stroke-width) * -0.9239) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.7071) calc(var(--stroke-width) * -0.7071) 0
          var(--stroke-color),
        calc(var(--stroke-width) * 0.9239) calc(var(--stroke-width) * -0.3827) 0
          var(--stroke-color);
    }

    span {
      /* margin-left: calc(100% + 1rem); */
      white-space: nowrap;
      position: absolute;
      top: 0;
      z-index: 5;

      &.SVP,
      &.UDC {
        color: #4b8a3e;
      }
      &.SP,
      &.PS {
        color: #f0554d;
      }
      &.Mitte,
      &.Centre {
        color: #d6862b;
      }
      &.FDP,
      &.PLR {
        color: #3872b5;
      }
      &.GPS,
      &.Grüne,
      &.Grünen,
      &.Verts {
        color: #84b547;
      }
      &.GLP,
      &.Vert’libéraux,
      &.PVL {
        color: #c4c43d;
      }
      &.Andere,
      &.Autres {
        color: #7d7d7d;
      }
    }

    &.byline.top:nth-of-type(1):not(.noTitle) {
      padding-bottom: 2em;
      border-left: 1px solid;
      @media only screen and (max-width: 599px) {
        padding-bottom: 1.75em;
      }
    }
    &.byline.top:nth-of-type(3) {
      padding-bottom: 4.5em;
      border-left: 1px solid;
      @media only screen and (max-width: 599px) {
        padding-bottom: 4.25em;
      }
    }

    &.byline.top:nth-of-type(5) {
      padding-bottom: 3.25em;
      border-left: 1px solid;
      @media only screen and (max-width: 599px) {
        padding-bottom: 3em;
      }
    }

    &.byline.top:nth-of-type(7) {
      padding-bottom: 2em;
      z-index: 3;
      border-left: 1px solid;
      @media only screen and (max-width: 599px) {
        padding-bottom: 1.75em;
      }
    }

    &.byline.bottom:nth-of-type(2),
    &.byline.bottom:nth-of-type(6) {
      padding-top: 2em;
      border-left: 1px solid;
      span {
        top: 2em;
      }
      @media only screen and (max-width: 599px) {
        padding-top: 1.75em;
        span {
          top: 1.75em;
        }
      }
    }
    &.byline.bottom:nth-of-type(6) {
      padding-top: 3.25em;
      border-left: 1px solid;
      span {
        top: 3.25em;
      }
      @media only screen and (max-width: 599px) {
        padding-top: 3em;
        span {
          top: 3em;
        }
      }
    }
    &.byline.bottom:nth-of-type(6) {
      z-index: 3;
    }
    &.byline.bottom:nth-of-type(4) {
      padding-top: 4.5em;
      border-left: 1px solid;
      span {
        top: 4.5em;
      }
      @media only screen and (max-width: 599px) {
        padding-top: 4.25em;
        span {
          top: 4.25em;
        }
      }
    }
    &.byline.bottom:nth-of-type(8) {
      padding-top: 2em;
      border-left: 1px solid;
      opacity: 0.25;
      span {
        top: 2em;
      }
      @media only screen and (max-width: 599px) {
        padding-top: 1.75em;
        span {
          top: 1.75em;
        }
      }
    }
    &.byline.bottom:nth-of-type(4) {
    }
  }
`

const ChartWrapper = styled.div`
  display: flex;

  .bar {
    box-sizing: border-box;
    padding: 1rem 1rem;
    line-height: 1em;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    /* transition: width 0.4s ease;
    min-height: 62px;
    max-height: 62px; */

    color: var(--brandblue-main);
    &.dark {
      color: var(--gray-1);
    }

    @media only screen and (max-width: 599px) {
      padding: 0.75rem 0.75rem;
      min-height: 65.5px;
      max-height: 65.5px;
    }
    span {
      /* animation: fadeIn .4s ease;
      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      } */

      /* line-height: 1em; */
    }
    span:first-of-type {
      font-size: 1.1em;
      margin-bottom: 0.5rem;
      @media only screen and (max-width: 599px) {
        font-size: 1em;
        margin-bottom: 0.35rem;
      }
    }

    span:not(:first-of-type) {
      font-size: 1.5rem;
      display: block;
      line-height: 1.25em;
      @media only screen and (min-width: 600px) and (max-width: 1024px) {
        font-size: 1.35rem;
      }
      @media only screen and (max-width: 599px) {
        font-size: 1.2rem;
      }
      @media only screen and (max-width: 330px) {
        font-size: 1.1rem;
      }
    }
    &.noTitle {
      padding: calc(1rem / 7);
      color: transparent !important;
      border: none !important;
      @media only screen and (max-width: 599px) {
        padding: calc(0.75rem / 7);
      }
    }

    &.fadeOut {
      opacity: 0.56;
      /* &.dark {
        opacity: 0.6; 
      } */
      &.dark:last-of-type {
        opacity: 0.35;
      }
    }
  }
  .bar:first-of-type {
    font-weight: 600;
    /* box-shadow: rgba(50, 50, 93, 0.25) 0px 0px 4px -1px, rgba(0, 0, 0, 0.3) 0px 0px 2px -1px;
    box-shadow: rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px; */

    /* &:not(.byline) {
      color: #fff;
    } */
  }
  .bar:not(:first-of-type).dark {
    &:not(.byline) {
      color: var(--gray-1);
    }
  }
  /* :first-of-type {
    color: #fff;
  } */
  /* styles */
`

export default Chapter
